import { getIconUnicode, ospThemes } from "@ospweb/sdk-ui";
import { useCallback, useEffect, useMemo, useRef } from "react";

import log from "logbeacon";
import { PerformanceCardProps } from "./types";
import { formatBigNumber } from "@/src/utils";
import { ILeft, createDualYAxisLineChart } from "@/src/components/createChart";

const PROFIT = "Profit";

export const usePerformance = (params: Pick<PerformanceCardProps, "data">) => {
  const { data } = params;
  const chartRef = useRef<HTMLDivElement>(null);

  const ticks = useMemo(() => {
    if (!data?.XAxis) {
      return { xKeyTicks: [] };
    }
    const step = Math.floor(data.XAxis.length / 4);
    const xKeyTicks = [
      data.XAxis[step],
      data.XAxis[step * 2],
      data.XAxis[step * 3],
    ];
    log.debug("[usePerformance] ticks", xKeyTicks);
    return { xKeyTicks };
  }, [data?.XAxis]);

  const pushYAxis = useCallback(
    (config: ILeft[number], arr: ILeft, data?: number[]) => {
      if (Array.isArray(data) && data.length > 0) {
        config.data = data;
        arr.push(config);
      }
      return arr;
    },
    [],
  );

  // Y轴刻度格式化函数：显示百分比
  const percentageFormatter = useCallback((ticks: number[]) => {
    return ticks.reduce(
      (map, tick) => {
        const percentage = tick.toFixed(0);
        map[tick] = tick >= 0 ? `+${percentage}%` : `${percentage}%`;
        return map;
      },
      {} as Record<number, string>,
    );
  }, []);

  const leftYAxis = useMemo(() => {
    const left: ILeft = [];
    const base = { lineWidth: 2, data: [], shadow: false };
    pushYAxis(
      {
        name: PROFIT,
        color: ospThemes.colors.content_brand_primary, // 青色
        ...base,
        shadow: true,
      },
      left,
      data?.YPnl,
    );
    pushYAxis(
      {
        name: "BTC trend",
        color: ospThemes.colors.content_accent1_primary, // 绿色
        ...base,
      },
      left,
      data?.YBtc,
    );
    pushYAxis(
      {
        name: "S&P 500",
        color: ospThemes.colors.content_accent3_primary, // 橙色
        ...base,
      },
      left,
      data?.YGspc,
    );
    return { left };
  }, [pushYAxis, data?.YPnl, data?.YBtc, data?.YGspc]);

  const tooltipFormatter = useCallback(
    (x: number) => {
      if (!data) {
        return "";
      }
      const timeLabel = data.XAxis[x]?.label ?? "";
      // 三条系列数据在 x 位置的值
      const series = leftYAxis.left.map<{
        name: string;
        color: string;
        percentage: number;
        value: number | null;
        direction: "rise" | "fall" | "normal";
      }>((item) => {
        const pnl = data.dataValue[x].pnl;
        const gspc = data.dataValue[x].gspc;
        const btc = data.dataValue[x].btc;
        const target =
          item.name === PROFIT ? pnl : item.name === "S&P 500" ? gspc : btc;
        const percent = target?.percentage ?? 0;
        const value = target?.value ?? 0;
        const direction =
          percent > 0 ? "rise" : percent < 0 ? "fall" : "normal";
        return {
          name: item.name,
          color: item.color,
          percentage: percent,
          value: item.name === PROFIT ? value : null,
          direction: direction,
        };
      });
      const classNameMap = {
        container:
          "p-3 rounded-2 bg-background_solid_neutral_tertiary border border-solid border-border_white_transparent10 w-47.5 md:w-55 flex flex-col gap-3",
        time: "body_medium_xs text-content_tertiary",
        itemWrapper: "flex flex-col gap-2",
        item: "w-full flex items-center justify-between",
        itemLabel:
          "body_medium_s text-content_primary flex gap-1 items-center before:content-[''] before:w-1 before:h-1 before:rounded-full before:bg-[var(--point-color)]",
        itemValue: "text-[var(--value-color)] flex items-center gap-1",
        itemChange: "body_s",
        itemsIcon: "rotate-[var(--icon-rotate)] font-[OspIcons] font-semibold",
        itemPrencent: "body_medium_s",
      };
      // 趋势
      const trendMap = {
        rise: {
          text: getIconUnicode("upvote"),
          color: ospThemes.colors.content_accent1_primary,
          sign: "+",
          rotate: "0deg",
        },
        fall: {
          text: getIconUnicode("upvote"),
          color: ospThemes.colors.content_accent2_primary,
          sign: "-",
          rotate: "180deg",
        },
        normal: {
          text: "",
          color: ospThemes.colors.content_secondary,
          sign: "",
          rotate: "0deg",
        },
      };
      const rows = series
        .map((s) => {
          let valueDisplay =
            s.name === PROFIT
              ? formatBigNumber(Math.abs(s.value || 0), {
                  needFormat: true,
                  precision: 2,
                  trimZeros: true,
                })
              : "";
          valueDisplay = valueDisplay
            ? `${trendMap[s.direction].sign}$${valueDisplay}`
            : "";
          const changeDisplay = formatBigNumber(Math.abs(s.percentage), {
            needFormat: true,
            precision: 2,
            trimZeros: true,
          });
          return `
          <div class="${classNameMap.item}">
            <span class="${classNameMap.itemLabel}" style="--point-color: ${s.color};">
              ${s.name}
            </span>
            <p class="${classNameMap.itemValue}" style="--value-color: ${trendMap[s.direction].color};">
              <span class="${classNameMap.itemChange}">${valueDisplay}</span>
              <span class="${classNameMap.itemsIcon}" style="--icon-rotate: ${trendMap[s.direction].rotate}">${trendMap[s.direction].text}</span>
              <span class="${classNameMap.itemPrencent}">${changeDisplay}%</span>
            </p>
          </div>
        `;
        })
        .join("");
      return `
      <div class="${classNameMap.container}">
        <div class="${classNameMap.time}">
          ${timeLabel}
        </div>
        <div class="${classNameMap.itemWrapper}">
          ${rows}
        </div>
      </div>
    `;
    },
    [data?.XAxis, leftYAxis, data?.dataValue],
  );

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }
    if (!data?.XAxis) {
      return;
    }
    const result = createDualYAxisLineChart({
      XAxis: data.XAxis,
      YAxis: leftYAxis,
      config: {
        el: chartRef.current,
        // Y轴刻度格式化
        yAxisTickFormatter: percentageFormatter,
        // tooltip 值格式化（用于默认tooltip和Y轴十字线标签）
        tooltipValueFormat: (value: MaybeOrNull<number>) => {
          if (value === null) {
            return "";
          }
          const percentage = value.toFixed(2);
          return value >= 0 ? `+${percentage}%` : `${percentage}%`;
        },
        // 自定义 tooltip HTML 内容（仅接收 x 索引）
        tooltipFormatter,
      },
      ticks,
    });

    return () => {
      result?.cleanup?.();
    };
  }, [leftYAxis, percentageFormatter, tooltipFormatter, ticks, data]);
  return {
    chartRef,
    leftYAxis,
  };
};
